import { Component, EventEmitter, Input, OnInit, Optional, Output } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { Client, ClientPhone } from 'src/typings';
import {
  applyPhonesToClient,
  formatClientPhone,
  getClientPhones,
  getClientPrimaryPhone
} from '../client-phone.util';
import { ClientService } from '../client.service';

export const CLIENT_DIALOG_CONFIG: MatDialogConfig = {
  width: '820px',
  maxWidth: 'calc(100vw - 24px)',
  maxHeight: 'calc(100dvh - 24px)',
  panelClass: 'client-edit-dialog',
  autoFocus: false
};

export function normalizeClientPhone(value: unknown) {
  let digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('38')) {
    digits = digits.slice(2);
  }
  if (digits.length === 11 && digits.startsWith('8')) {
    digits = digits.slice(1);
  }
  return digits;
}

export function clientPhoneValidator(control: AbstractControl): ValidationErrors | null {
  const phone = normalizeClientPhone(control.value);
  return !phone || phone.length === 10 ? null : { phone: true };
}

@Component({
  selector: 'app-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.scss']
})
export class CreateComponent implements OnInit {
  constructor(
    private clientService: ClientService,
    private notificationService: NotificationService,
    private fb: UntypedFormBuilder,
    @Optional() private dialogRef?: MatDialogRef<CreateComponent>
  ) {}

  @Input() set client(payload: Client | undefined) {
    this._client = payload;
    if (payload?.id) {
      this.phoneFormControl.disable({ emitEvent: false });
    } else {
      this.phoneFormControl.enable({ emitEvent: false });
    }
    this.nameFormControl.setValue(payload?.name);
    this.phoneFormControl.setValue(normalizeClientPhone(payload?.phone));
    this.ratingFormControl.setValue(payload?.rating);
    this.solvencyFormControl.setValue(payload?.solvency);
    this.smsNotificationsEnabledFormControl.setValue(payload?.smsNotificationsEnabled !== false);
    this.paymentDelayDaysFormControl.setValue(payload?.paymentDelayDays ?? null);
    this.debtLimitFormControl.setValue(payload?.debtLimit ?? null);
    this.syncPhonesFromClient();
    if (payload?.id) this.refreshPhones();
  }

  private _client: Client | undefined;

  get client() {
    return this._client;
  }

  @Input() control: UntypedFormControl | undefined;

  @Output() createClient: EventEmitter<Client> = new EventEmitter<Client>();

  @Output() clientChanged: EventEmitter<Client> = new EventEmitter<Client>();

  addPhoneFormControl = new UntypedFormControl('', [clientPhoneValidator]);

  editPhoneFormControl = new UntypedFormControl('', [clientPhoneValidator]);

  editingPhoneId: number | null = null;

  saving = false;

  phoneActionLoading = false;

  formGroup = this.fb.group({
    name: new UntypedFormControl(),
    phone: new UntypedFormControl('', [clientPhoneValidator]),
    solvency: new FormControl(0),
    rating: new FormControl(0),
    smsNotificationsEnabled: new UntypedFormControl(true),
    paymentDelayDays: new UntypedFormControl(null, [
      Validators.min(0),
      Validators.pattern(/^\d+$/)
    ]),
    debtLimit: new UntypedFormControl(null, [Validators.min(0)])
  });

  ngOnInit(): void {
    const normalizedPhone = normalizeClientPhone(this.control?.value);
    if (this.control && normalizedPhone.length === 10) {
      this.phoneFormControl.setValue(normalizedPhone);
    } else if (this.control) {
      this.nameFormControl.setValue(this.control.value);
    }
  }

  get name() {
    return this.formGroup.controls.name.value;
  }

  get rating() {
    return this.formGroup.controls.rating.value;
  }

  get solvency() {
    return this.formGroup.controls.solvency.value;
  }

  get smsNotificationsEnabled() {
    return this.formGroup.controls.smsNotificationsEnabled.value !== false;
  }

  get paymentDelayDays() {
    const value = this.formGroup.controls.paymentDelayDays.value;
    return value === undefined || value === null || value === '' ? null : Number(value);
  }

  get debtLimit() {
    const value = this.formGroup.controls.debtLimit.value;
    return value === undefined || value === null || value === '' ? null : Number(value);
  }

  get nameFormControl() {
    return this.formGroup.controls.name as UntypedFormControl;
  }

  get phone() {
    return normalizeClientPhone(this.formGroup.controls.phone.value);
  }

  get phoneFormControl() {
    return this.formGroup.controls.phone as UntypedFormControl;
  }

  get ratingFormControl() {
    return this.formGroup.controls.rating as UntypedFormControl;
  }

  get solvencyFormControl() {
    return this.formGroup.controls.solvency as UntypedFormControl;
  }

  get smsNotificationsEnabledFormControl() {
    return this.formGroup.controls.smsNotificationsEnabled as UntypedFormControl;
  }

  get paymentDelayDaysFormControl() {
    return this.formGroup.controls.paymentDelayDays as UntypedFormControl;
  }

  get debtLimitFormControl() {
    return this.formGroup.controls.debtLimit as UntypedFormControl;
  }

  get phones(): ClientPhone[] {
    return getClientPhones(this.client);
  }

  get isEditMode() {
    return !!this.client;
  }

  get title() {
    return this.isEditMode ? 'Редактирование клиента' : 'Новый клиент';
  }

  get subtitle() {
    return this.isEditMode && this.client?.id ? `Клиент №${this.client.id}` : 'Карточка клиента';
  }

  useDefaultName() {
    if (this.saving || this.isEditMode) return;
    this.nameFormControl.setValue('Клиент');
    this.nameFormControl.markAsDirty();
  }

  formatPhone(phone?: string | null) {
    return formatClientPhone(phone);
  }

  startEditPhone(phone: ClientPhone) {
    this.editingPhoneId = phone.id;
    this.editPhoneFormControl.setValue(phone.phone);
  }

  cancelEditPhone() {
    this.editingPhoneId = null;
    this.editPhoneFormControl.setValue('');
  }

  async refreshPhones() {
    if (!this.client) return;
    try {
      const phones = await firstValueFrom(this.clientService.listPhones(this.client.id));
      this.setClientPhones(phones);
    } catch (error: unknown) {
      this.showApiError(error, 'Ошибка загрузки телефонов');
    }
  }

  async addPhone() {
    if (!this.client || this.phoneActionLoading) return;
    if (this.addPhoneFormControl.invalid) {
      this.addPhoneFormControl.markAsTouched();
      return this.notificationService.showError('Проверьте номер телефона');
    }
    this.phoneActionLoading = true;
    try {
      const phones = await firstValueFrom(
        this.clientService.addPhone(this.client.id, {
          phone: this.addPhoneFormControl.value
        })
      );
      this.setClientPhones(phones);
      this.addPhoneFormControl.setValue('');
      this.emitClientChanged();
    } catch (error: unknown) {
      this.showApiError(error, 'Ошибка добавления телефона');
    } finally {
      this.phoneActionLoading = false;
    }
  }

  async savePhone(phoneId: number) {
    if (!this.client || this.phoneActionLoading) return;
    if (this.editPhoneFormControl.invalid) {
      this.editPhoneFormControl.markAsTouched();
      return this.notificationService.showError('Проверьте номер телефона');
    }
    this.phoneActionLoading = true;
    try {
      const phones = await firstValueFrom(
        this.clientService.updatePhone(this.client.id, phoneId, {
          phone: this.editPhoneFormControl.value
        })
      );
      this.setClientPhones(phones);
      this.cancelEditPhone();
      this.emitClientChanged();
    } catch (error: unknown) {
      this.showApiError(error, 'Ошибка сохранения телефона');
    } finally {
      this.phoneActionLoading = false;
    }
  }

  async makePrimary(phoneId: number) {
    if (!this.client || this.phoneActionLoading) return;
    this.phoneActionLoading = true;
    try {
      const phones = await firstValueFrom(
        this.clientService.setPrimaryPhone(this.client.id, phoneId)
      );
      this.setClientPhones(phones);
      this.emitClientChanged();
    } catch (error: unknown) {
      this.showApiError(error, 'Ошибка обновления телефона');
    } finally {
      this.phoneActionLoading = false;
    }
  }

  async removePhone(phoneId: number) {
    if (!this.client || this.phoneActionLoading || !window.confirm('Удалить номер телефона?')) return;
    this.phoneActionLoading = true;
    try {
      const phones = await firstValueFrom(this.clientService.deletePhone(this.client.id, phoneId));
      this.setClientPhones(phones);
      if (this.editingPhoneId === phoneId) this.cancelEditPhone();
      this.emitClientChanged();
    } catch (error: unknown) {
      this.showApiError(error, 'Ошибка удаления телефона');
    } finally {
      this.phoneActionLoading = false;
    }
  }

  onFormEnter(event: KeyboardEvent) {
    if (event.key === 'Enter' && (event.repeat || event.isComposing)) event.preventDefault();
  }

  async save() {
    if (this.saving || this.phoneActionLoading) return;
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return this.notificationService.showError('Проверьте поля формы');
    }
    if (this.client) return this.edit();
    if (!this.client) return this.create();
  }

  async edit() {
    if (!this.client) return;
    this.saving = true;
    if (this.dialogRef) this.dialogRef.disableClose = true;
    try {
      const res = await firstValueFrom(
        this.clientService.update({
          id: this.client.id,
          name: this.name,
          rating: this.rating,
          solvency: this.solvency,
          smsNotificationsEnabled: this.smsNotificationsEnabled,
          paymentDelayDays: this.paymentDelayDays,
          debtLimit: this.debtLimit
        })
      );
      if (res.error) {
        return this.notificationService.showError(String(res.message));
      }
      this._client = {
        ...this.client,
        ...res.message
      };
      this.syncPhonesFromClient();
      this.createClient.emit(this._client);
    } catch (error) {
      this.showApiError(error, 'Не удалось сохранить клиента');
    } finally {
      this.saving = false;
      if (this.dialogRef) this.dialogRef.disableClose = false;
    }
  }

  async create() {
    if (!String(this.name || '').trim()) {
      this.nameFormControl.setValue('Клиент');
    }
    this.saving = true;
    if (this.dialogRef) this.dialogRef.disableClose = true;
    try {
      const newClient = await firstValueFrom(
        this.clientService.create(
          this.name,
          this.phone,
          this.rating,
          this.solvency,
          this.smsNotificationsEnabled,
          this.paymentDelayDays,
          this.debtLimit
        )
      );
      if (!newClient) return;
      this.createClient.emit(newClient);
    } catch (error) {
      this.showApiError(error, 'Не удалось создать клиента');
    } finally {
      this.saving = false;
      if (this.dialogRef) this.dialogRef.disableClose = false;
    }
  }

  close() {
    if (!this.saving && !this.phoneActionLoading) this.dialogRef?.close();
  }

  private syncPhonesFromClient() {
    if (!this.client) return;
    const primary = getClientPrimaryPhone(this.client);
    this.phoneFormControl.setValue(normalizeClientPhone(primary));
  }

  private setClientPhones(phones: ClientPhone[]) {
    if (!this.client) return;
    this._client = applyPhonesToClient(this.client, phones);
    this.phoneFormControl.setValue(normalizeClientPhone(getClientPrimaryPhone(this._client)));
  }

  private emitClientChanged() {
    if (this.client) this.clientChanged.emit(this.client);
  }

  private showApiError(error: unknown, fallback: string) {
    const errorMessage =
      typeof error === 'object' &&
      error &&
      'error' in error &&
      typeof (error as { error?: { message?: string } }).error?.message === 'string'
        ? (error as { error?: { message?: string } }).error?.message
        : fallback;
    this.notificationService.showError(errorMessage || fallback);
  }
}
